import React, { useState, useEffect, useMemo } from 'react'
import {
  Card,
  Button,
  Result,
  Space,
  Typography,
  Spin,
  message,
  Switch,
  Modal,
  Tag,
  Divider,
  Input
} from 'antd'
import {
  MessageOutlined,
  LinkOutlined,
  ReloadOutlined,
  CopyOutlined,
  DisconnectOutlined,
  BellOutlined,
  UserOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import Hint from '../components/common/Hint'
import { formatTz } from '../utils/timezone'

const { Title, Text, Paragraph } = Typography

const PageContainer = styled.div`
  padding: 24px;
  background: #fff;
  min-height: 100vh;
  @media (max-width: 768px) {
    padding: 16px;
  }
`

const StyledCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`

const PrefRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
  }
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`

const QRBox = styled.div`
  text-align: center;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
`

const MyLineNotificationPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState({
    me: null,
    isLinked: false,
    lineLinkedAt: null,
    preferences: {
      notifyOnAssignedBooking: true,
      notifyOnAllBookings: false
    },
    pendingPairing: null
  })
  const [requesting, setRequesting] = useState(false)
  const [unbinding, setUnbinding] = useState(false)

  const businessId = user?.businessId

  const fetchStatus = async () => {
    if (!businessId) return
    setLoading(true)
    try {
      const resp = await api.get(
        `/api/employees/${businessId}/me/line-status`
      )
      if (resp.data?.success) {
        setStatus({
          me: resp.data.me || null,
          isLinked: !!resp.data.isLinked,
          lineLinkedAt: resp.data.lineLinkedAt,
          preferences: resp.data.preferences || {
            notifyOnAssignedBooking: true,
            notifyOnAllBookings: false
          },
          pendingPairing: resp.data.pendingPairing || null
        })
      }
    } catch (err) {
      console.error('取得 LINE 通知狀態失敗:', err)
      message.error('取得狀態失敗')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId])

  const handleRequestCode = async () => {
    setRequesting(true)
    try {
      const resp = await api.post(
        `/api/employees/${businessId}/me/line-pairing`
      )
      if (resp.data?.success) {
        setStatus((prev) => ({
          ...prev,
          pendingPairing: {
            pairingCode: resp.data.pairingCode,
            expiresAt: resp.data.expiresAt
          }
        }))
        message.success('已產生配對碼')
      }
    } catch (err) {
      const code = err?.response?.data?.code
      const msg = err?.response?.data?.error
      if (code === 'ALREADY_PAIRED') {
        message.warning(msg || '您已綁定 LINE，請先解除再重新綁定')
      } else if (code === 'LINE_NOT_ENABLED') {
        Modal.confirm({
          title: '尚未啟用店家 LINE 官方帳號',
          icon: null,
          content: (
            <div>
              <p style={{ marginBottom: 8 }}>
                員工 LINE 通知需要店家先在「設定 → LINE 整合」完成自家 LINE Channel
                的設定（channel ID / secret / access token）並啟用。
              </p>
              <p style={{ marginBottom: 0, color: '#8c8c8c', fontSize: 12 }}>
                如果您不是負責人，請通知店長或管理員前往「LINE 整合」頁完成設定。
              </p>
            </div>
          ),
          okText: '前往 LINE 整合設定',
          cancelText: '稍後再說',
          onOk: () => navigate('/settings/line')
        })
      } else {
        message.error(msg || '產生配對碼失敗')
      }
    } finally {
      setRequesting(false)
    }
  }

  const handleUpdatePref = async (key, value) => {
    const next = { ...status.preferences, [key]: value }
    setStatus((prev) => ({ ...prev, preferences: next }))
    try {
      await api.put(
        `/api/employees/${businessId}/me/line-preferences`,
        { [key]: value }
      )
      message.success('已更新通知偏好')
    } catch (err) {
      console.error('更新偏好失敗:', err)
      message.error('更新失敗')
      // 回滾
      setStatus((prev) => ({
        ...prev,
        preferences: { ...prev.preferences, [key]: !value }
      }))
    }
  }

  const handleUnbind = () => {
    Modal.confirm({
      title: '解除 LINE 綁定？',
      content:
        '解除後您將不再收到此商家的預約通知，需重新產生配對碼才能再綁定。',
      okText: '確定解除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        setUnbinding(true)
        try {
          await api.delete(
            `/api/employees/${businessId}/me/line-binding`
          )
          message.success('已解除綁定')
          await fetchStatus()
        } catch (err) {
          console.error('解除綁定失敗:', err)
          message.error('解除失敗')
        } finally {
          setUnbinding(false)
        }
      }
    })
  }

  const handleCopyCode = async () => {
    const code = status.pendingPairing?.pairingCode
    if (!code) return
    try {
      await navigator.clipboard.writeText(code)
      message.success('已複製配對碼')
    } catch {
      message.warning('複製失敗，請手動複製')
    }
  }

  const expiresInMin = useMemo(() => {
    const exp = status.pendingPairing?.expiresAt
    if (!exp) return null
    const ms = new Date(exp).getTime() - Date.now()
    return ms > 0 ? Math.ceil(ms / 60000) : 0
  }, [status.pendingPairing])

  if (loading) {
    return (
      <PageContainer>
        <div style={{ padding: 50, textAlign: 'center' }}>
          <Spin size='large' />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <Title level={2} style={{ marginBottom: 24 }}>
        <MessageOutlined /> 我的 LINE 通知
      </Title>

      {/* 顯眼提示：到底正在綁誰，避免共用電腦 / 老闆代開時搞錯人 */}
      {status.me && (
        <Hint
          title={
            <Space>
              <UserOutlined />
              <span>
                目前正在設定的人是
                <Text strong style={{ marginLeft: 6 }}>
                  {status.me.name}
                </Text>
                <Text type='secondary' style={{ marginLeft: 6 }}>
                  ({status.me.email})
                </Text>
              </span>
            </Space>
          }
          tone='warning'
          style={{ marginBottom: 16 }}
        >
          系統依您目前登入的後台帳號決定要綁定誰。如果不是您本人，請先登出後改用您自己的帳號登入再來綁定。
        </Hint>
      )}

      <Hint
        title='這是「個人」綁定'
        tone='info'
        style={{ marginBottom: 16 }}
      >
        綁定的是您本人的 LINE 帳號，給「店家自己的 LINE 官方帳號」（不是美感平台的 LINE）。綁定後，當您被指派為預約造型師時，您的 LINE 會直接收到通知。每位員工各自綁定，不會收到別人的客戶通知。
      </Hint>

      {/* 綁定狀態 */}
      <StyledCard
        title={
          <Space>
            <LinkOutlined />
            <span>LINE 綁定狀態</span>
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            size='small'
            onClick={fetchStatus}
          >
            重新整理
          </Button>
        }
      >
        {status.isLinked ? (
          <Result
            status='success'
            title='LINE 已綁定'
            subTitle={
              status.lineLinkedAt
                ? `綁定時間：${formatTz(
                    status.lineLinkedAt,
                    'YYYY/MM/DD HH:mm'
                  )}`
                : null
            }
            extra={[
              <Button
                key='unbind'
                danger
                icon={<DisconnectOutlined />}
                loading={unbinding}
                onClick={handleUnbind}
              >
                解除綁定
              </Button>
            ]}
          />
        ) : status.pendingPairing ? (
          <div>
            <Paragraph>
              <Text strong>請依以下步驟完成綁定：</Text>
            </Paragraph>
            <ol style={{ marginBottom: 16 }}>
              <li>
                加入<b>本店自己申請的 LINE 官方帳號</b>為好友
                （不是美感平台的 LINE，請向店長索取店家 OA 的 QR / ID）
              </li>
              <li>
                在<b>店家 OA</b>的對話框輸入下方配對碼，並按下發送
              </li>
              <li>看到「員工 LINE 通知已綁定」回覆訊息即代表完成</li>
            </ol>

            <Space
              direction='vertical'
              size='large'
              style={{ width: '100%' }}
            >
              <QRBox>
                <Text type='secondary'>
                  請複製或手動輸入下方配對碼到「店家 LINE 官方帳號」對話框
                </Text>
              </QRBox>

              <Input.Group compact>
                <Input
                  value={status.pendingPairing.pairingCode}
                  readOnly
                  style={{
                    width: 'calc(100% - 80px)',
                    fontSize: 18,
                    letterSpacing: 2,
                    fontFamily: 'monospace',
                    textAlign: 'center'
                  }}
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={handleCopyCode}
                  style={{ width: 80 }}
                >
                  複製
                </Button>
              </Input.Group>

              <div>
                {expiresInMin != null && (
                  <Tag color={expiresInMin > 5 ? 'blue' : 'orange'}>
                    剩餘 {expiresInMin} 分鐘有效
                  </Tag>
                )}
                <Button
                  size='small'
                  onClick={handleRequestCode}
                  loading={requesting}
                  style={{ marginLeft: 8 }}
                >
                  重新產生
                </Button>
              </div>
            </Space>
          </div>
        ) : (
          <Result
            icon={<MessageOutlined style={{ color: '#1890ff' }} />}
            title='尚未綁定 LINE'
            subTitle='點下方按鈕產生配對碼，然後到店家的 LINE 官方帳號輸入'
            extra={[
              <Button
                key='req'
                type='primary'
                onClick={handleRequestCode}
                loading={requesting}
              >
                產生配對碼
              </Button>
            ]}
          />
        )}
      </StyledCard>

      {/* 通知偏好（綁定後才有意義，但仍允許先設） */}
      <StyledCard
        title={
          <Space>
            <BellOutlined />
            <span>通知偏好</span>
          </Space>
        }
      >
        <PrefRow>
          <div>
            <div>
              <Text strong>被指派為造型師時通知我</Text>
            </div>
            <Text type='secondary' style={{ fontSize: 12 }}>
              客戶選您為造型師的新預約，會推播給您。建議保持開啟。
            </Text>
          </div>
          <Switch
            checked={!!status.preferences.notifyOnAssignedBooking}
            onChange={(v) =>
              handleUpdatePref('notifyOnAssignedBooking', v)
            }
          />
        </PrefRow>

        <PrefRow>
          <div>
            <div>
              <Text strong>店內任何新預約都通知我</Text>
            </div>
            <Text type='secondary' style={{ fontSize: 12 }}>
              店長 / 櫃台 / 行政可以打開，會收到全店所有預約。一般員工建議關閉。
            </Text>
          </div>
          <Switch
            checked={!!status.preferences.notifyOnAllBookings}
            onChange={(v) => handleUpdatePref('notifyOnAllBookings', v)}
          />
        </PrefRow>

        <Divider />
        <Paragraph type='secondary' style={{ marginBottom: 0, fontSize: 12 }}>
          兩個都關 = 不會收到 LINE 通知（仍會在後台看到預約紀錄）。
        </Paragraph>
      </StyledCard>
    </PageContainer>
  )
}

export default MyLineNotificationPage
